import { createSelector } from 'reselect'
import * as R from 'ramda'
import { Store } from '../index'

import {
  BeamSchema,
  SlabSchema,
  LoadCaseSchema
} from '../../Components/Workspace/Calculation/validationSchema'

import {
  EditableBeam,
  EditableSlab,
  EditableLoadCase,
  EditableCase,
  EditableLoad
} from '../projects/projectTypes'

/*
  All project-related selectors
 */

// Total number of categories, fixed for now (location, spacetype, beams, slabs & loads)
const NumCategories = 5

export const getProjectId = (state: Store.Complete) => state.project.projectId

export const getProjectName = (state: Store.Complete) => state.project.name

export const getProjectLocation = (state: Store.Complete) =>
  state.project.location

export const getIsNewProject = (state: Store.Complete) => state.project.new

export const getProjectError = (state: Store.Complete) =>
  state.project.projectError

export const getProjectDefaultSpaceType = (state: Store.Complete) =>
  state.project.defaultSpaceType

export const getProjectThumbnail = (state: Store.Complete) =>
  state.project.thumbnailData

export const getSelectedCaseId = R.path<string | null>([
  'project',
  'activeCase'
])

export const getEditableCases = createSelector(
  R.path<EditableCase[]>(['project', 'cases']),
  cases => cases.filter(kase => !kase._deleted)
)

// As before, but deleted cases are included
export const getAllCases = createSelector(
  R.path<EditableCase[]>(['project', 'cases']),
  cases => cases
)

/* Item selectors */

/* TODO: Improve the quality of the selectors here. We don't actually benefit that much from
   memoization yet because everything is packed under state.project.cases which changes frequently.
   We could either flatten our state tree a bit or be a bit smarter about the memoization
   functions used (i.e. use custom ones). */

export const getActiveCase = createSelector<
  Store.Complete,
  EditableCase[],
  string | null,
  EditableCase | undefined
>(
  R.path<EditableCase[]>(['project', 'cases']),
  R.path(['project', 'activeCase']),
  (cases, activeCaseId) =>
    R.find(R.propEq('id', activeCaseId), cases.filter(kase => !kase._deleted))
)

export const getSelectedItemId = createSelector(
  getActiveCase,
  // Always return null for now to support some previous code.
  kase => (kase ? (kase.activeItem ? kase.activeItem : null) : null)
)

// This is obviously no good for the long term (when we do multiple beams) but should be good
// enough for now.
export const getFirstBeam = createSelector<
  Store.Complete,
  EditableCase | undefined,
  EditableBeam | undefined
>(getActiveCase, kase => (kase ? kase.beams[0] : undefined))

export const getSlabs = createSelector<
  Store.Complete,
  EditableCase | undefined,
  EditableSlab[]
>(getActiveCase, kase => (kase ? kase.slabs : []))

export const getActiveSlabs = createSelector<
  Store.Complete,
  EditableCase | undefined,
  EditableSlab[]
>(
  getActiveCase,
  kase => (kase ? R.reject(R.propEq('disabled', true), kase.slabs) : [])
)

export function findActiveCategory(
  activeCase: EditableCase,
  activeItemId: string | null | undefined
): string | undefined {
  if (!activeCase) {
    return undefined
  }

  if (activeItemId === 'LOCATION') {
    return 'location'
  }

  if (activeItemId === 'SPACE_TYPE') {
    return 'spacetype'
  }

  const findByActiveId = R.propEq('id', activeItemId)

  if (R.find(findByActiveId, activeCase.beams)) {
    return 'beams'
  }

  if (R.find(findByActiveId, activeCase.slabs)) {
    return 'slabs'
  }

  if (R.find(findByActiveId, activeCase.loadCases)) {
    return 'loads'
  }

  return undefined
}

export const getActiveCategory = createSelector<
  Store.Complete,
  EditableCase | undefined,
  string | null,
  string | undefined
>(getActiveCase, getSelectedItemId, findActiveCategory)

export const getActiveSpaceType = createSelector(
  getActiveCase,
  (activeCase: EditableCase) => activeCase.spaceType
)

export const getActiveBeam = createSelector(
  getActiveCase,
  getSelectedItemId,
  (activeCase: EditableCase, activeItemId) =>
    R.find(R.propEq('id', activeItemId), activeCase.beams)
)

export const getActiveSlab = createSelector(
  getActiveCase,
  getSelectedItemId,
  (activeCase: EditableCase, activeItemId) =>
    R.find<EditableSlab>(R.propEq('id', activeItemId), activeCase.slabs)
)

export const getActiveLoadCase = createSelector<
  Store.Complete,
  EditableCase | undefined,
  string | null,
  EditableLoadCase | undefined
>(getActiveCase, getSelectedItemId, (activeCase: EditableCase, activeItemId) =>
  R.find(R.propEq('id', activeItemId), activeCase.loadCases)
)

function resolveAutoValues(
  load: EditableLoad,
  kase: EditableCase
): EditableLoad | null {
  if (!load.autoId) {
    return load
  }

  // Display "automatic" instead of the actual parameters
  if (load.autoId === 'beam.weight') {
    const beam = kase.beams[0]

    return { ...load, _auto: true, values: [[0, -2.5], [beam.length, -2.5]] }
  }

  // In future, we should have an actual system for resolving these.
  // slab.left.weight -> slabs, find element by name 'left' and pick property 'weight'
  if (load.autoId === 'slab.left.weight') {
    const leftSlab = R.find(R.propEq('name', 'left'), kase.slabs)
    if (!leftSlab || leftSlab.disabled) {
      return null
    }

    return { ...load, values: [[0, leftSlab.weight]] }
  }

  if (load.autoId === 'slab.right.weight') {
    const rightSlab = R.find(R.propEq('name', 'right'), kase.slabs)
    if (!rightSlab || rightSlab.disabled) {
      return null
    }

    return { ...load, values: [[0, rightSlab.weight]] }
  }

  return load
}

function isEditableLoad(load: EditableLoad | null): load is EditableLoad {
  return load !== null
}

// Returns a load case with auto values resolved to the values they point to. Auto value
// of slab.left.weight will have values set to left slab's weight and so forth.
export const getResolvedLoadCase = createSelector<
  Store.Complete,
  EditableCase | undefined,
  string | null,
  EditableLoadCase | undefined
>(
  getActiveCase,
  getSelectedItemId,
  (activeCase: EditableCase, activeItemId) => {
    if (!activeCase) {
      return undefined
    }

    const lc = R.find<EditableLoadCase>(
      R.propEq('id', activeItemId),
      activeCase.loadCases
    )
    if (!lc) {
      return undefined
    }

    const loads = lc.loads

    return {
      ...lc,
      loads: loads
        .map(l => resolveAutoValues(l, activeCase))
        .filter(isEditableLoad)
    }
  }
)

/* Validity selectors */

export const getBeamsValid = createSelector(
  getActiveCase,
  (activeCase: EditableCase) => {
    const predicate = beam => BeamSchema.isValidSync(beam)
    return R.all(predicate, activeCase.beams)
  }
)

export const getSlabsValid = createSelector(
  getActiveCase,
  (activeCase: EditableCase) => {
    const predicate = slab => SlabSchema.isValidSync(slab)
    return R.all(predicate, activeCase.slabs)
  }
)

export const getLoadsValid = createSelector(
  getActiveCase,
  (activeCase: EditableCase) => {
    const predicate = loadCase => LoadCaseSchema.isValidSync(loadCase)
    return R.all(predicate, activeCase.loadCases)
  }
)

export const getValidCategories = createSelector(
  getBeamsValid,
  getSlabsValid,
  getLoadsValid,
  (beams, slabs, loads) => {
    let validItems: string[] = ['spacetype', 'location']

    if (beams) {
      validItems.push('beams')
    }

    if (slabs) {
      validItems.push('slabs')
    }

    if (loads) {
      validItems.push('loads')
    }

    return validItems
  }
)

export const getAllValid = createSelector(
  getValidCategories,
  validCategories => validCategories.length === NumCategories
)

export const getTouchedCategories = createSelector(
  getActiveCase,
  (kase: EditableCase) => kase.touchedCategories
)

export const getAllTouchedAndValid = createSelector(
  getTouchedCategories,
  getAllValid,
  (touched, allValid) => allValid && touched.length === NumCategories
)

// Written as a selector because this might be a dynamic number in the future
export const getNumCategories = (state: Store.Complete) => NumCategories

/* Misc */

// This is a port from the old calc reducer
const InputSets = { location: 0, spacetype: 1, beams: 2, slabs: 3, loads: 4 }

export const getActiveInputSet = createSelector(
  getActiveCategory,
  activeCategory => {
    if (!activeCategory) {
      return [2]
    }

    return [InputSets[activeCategory]]
  }
)

export const getOneSided = createSelector(
  getActiveSlabs,
  slabs => slabs.length === 1
)

export const getDrTypeBeam = createSelector(
  getFirstBeam,
  beam => (beam ? beam.profileType === 'DR' : false)
)

export const getProjectDirty = (state: Store.Complete) => state.project.dirty



// WEBPACK FOOTER //
// ./src/reducers/projects/projectSelectors.ts